import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n, useT } from "@/i18n";
import Logo from "@/components/Logo";
import { Mail, Phone, MapPin } from "lucide-react";

export default function Footer({ onOpenCookiePrefs }) {
  const t = useT();
  const { lang } = useI18n();
  const location = useLocation();
  const homeHref = `/${lang}`;
  const onHome = location.pathname === homeHref || location.pathname === `${homeHref}/`;
  const anchor = (id) => (onHome ? `#${id}` : `${homeHref}#${id}`);

  return (
    <footer className="relative bg-[#0B0F14] border-t border-white/5 text-[#c8cdd6]" data-testid="site-footer">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Logo />
            <p className="mt-6 text-sm leading-relaxed max-w-sm">{t("footer.tagline")}</p>
            <p className="mt-6 text-xs leading-relaxed text-[#8f96a3]">{t("footer.disclaimer")}</p>
          </div>
          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#8f96a3] mb-4">
              {t("footer.exploreTitle")}
            </div>
            <ul className="space-y-2 text-sm">
              <li><a data-testid="footer-nav-about" href={anchor("about")} className="hover:text-white transition">{t("nav.about")}</a></li>
              <li><a data-testid="footer-nav-how" href={anchor("how")} className="hover:text-white transition">{t("nav.howItWorks")}</a></li>
              <li><a data-testid="footer-nav-benefits" href={anchor("benefits")} className="hover:text-white transition">{t("nav.benefits")}</a></li>
              <li><a data-testid="footer-nav-faq" href={anchor("faq")} className="hover:text-white transition">{t("nav.faq")}</a></li>
              <li><a data-testid="footer-nav-contact" href={anchor("contact")} className="hover:text-white transition">{t("nav.contact")}</a></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#8f96a3] mb-4">
              {t("footer.legalTitle")}
            </div>
            <ul className="space-y-2 text-sm">
              <li><Link data-testid="footer-link-privacy" to={`/${lang}/privacy`} className="hover:text-white transition">{t("policies.privacyLink")}</Link></li>
              <li><Link data-testid="footer-link-cookies" to={`/${lang}/cookies`} className="hover:text-white transition">{t("policies.cookiesLink")}</Link></li>
              <li><Link data-testid="footer-link-terms" to={`/${lang}/terms`} className="hover:text-white transition">{t("policies.termsLink")}</Link></li>
              <li>
                <button
                  type="button"
                  onClick={onOpenCookiePrefs}
                  data-testid="footer-cookie-prefs"
                  className="text-left hover:text-white transition"
                >
                  {t("cookies.manage")}
                </button>
              </li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#8f96a3] mb-4">
              {t("footer.contactTitle")}
            </div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <MapPin size={14} className="mt-0.5 text-[#C9F14B]" />
                <span>{t("footer.legal.address")}</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={14} className="text-[#C9F14B]" />
                <a data-testid="footer-email" href={`mailto:${t("footer.legal.email")}`} className="hover:text-white transition">{t("footer.legal.email")}</a>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={14} className="text-[#C9F14B]" />
                <a data-testid="footer-phone" href={`tel:${(t("footer.legal.phone") || "").replace(/\s/g, "")}`} className="hover:text-white transition">{t("footer.legal.phone")}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/5 grid grid-cols-1 md:grid-cols-12 gap-6 text-xs text-[#8f96a3]">
          <div className="md:col-span-8 space-y-1">
            <div className="text-[#F1ECDE] font-medium">{t("footer.companyName")}</div>
            <div>{t("footer.legal.cuit")} · {t("footer.legal.igj")}</div>
            <div>{t("footer.legal.jurisdiction")}</div>
            <div>{t("footer.legal.license")} · {t("footer.legal.founded")}</div>
          </div>
          <div className="md:col-span-4 md:text-right">{t("footer.rights")}</div>
        </div>
      </div>
    </footer>
  );
}
